import { FingerprintIcon, KeyIcon, PlusIcon, TrashIcon } from 'lucide-react';

import { useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  type PasskeyCredential,
  isPasskeySupported,
} from '@splitfiesta/platform-web/auth/cognito';
import { Alert, Button } from '@splitfiesta/ui';

import {
  useDeletePasskeyMutation,
  usePasskeysQuery,
  useRegisterPasskeyMutation,
} from '@/hooks/queries/usePasskeys';

import { useAuthStore } from '@/stores/authStore';

interface PasskeySettingsProps {
  'data-testid'?: string;
}

export function PasskeySettings({
  'data-testid': dataTestId,
}: PasskeySettingsProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get user email for passkey registration (for usernameless sign-in)
  const userEmail = useAuthStore(state => state.user?.email);

  // Load profile namespace for passkeys translations
  useNamespace('profile');

  const {
    data: passkeys = [],
    isLoading,
    isError,
  } = usePasskeysQuery(isPasskeySupported());

  const registerMutation = useRegisterPasskeyMutation(userEmail);
  const deleteMutation = useDeletePasskeyMutation();

  const handleRegister = async () => {
    setError(null);
    setSuccess(null);
    try {
      await registerMutation.mutateAsync();
      setSuccess(t('profile:passkeys.registerSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('profile:passkeys.registerError')
      );
    }
  };

  const handleDelete = async (credentialId: string) => {
    setError(null);
    setSuccess(null);
    try {
      await deleteMutation.mutateAsync(credentialId);
      setSuccess(t('profile:passkeys.deleteSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('profile:passkeys.deleteError')
      );
    }
  };

  // Intl.DateTimeFormat throws on an Invalid Date; such a row just
  // drops its "Added on" line.
  const addedOn = (date: Date) =>
    date instanceof Date && !Number.isNaN(date.getTime())
      ? t('profile:passkeys.addedOn', {
          date: new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(date),
        })
      : undefined;

  const getPasskeyIcon = (passkey: PasskeyCredential) => {
    if (passkey.authenticatorAttachment === 'platform') {
      return <FingerprintIcon className="w-5 h-5 text-orange-500" />;
    }
    return <KeyIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />;
  };

  const getPasskeyName = (passkey: PasskeyCredential, index: number) => {
    if (passkey.friendlyName) {
      return passkey.friendlyName;
    }
    if (passkey.authenticatorAttachment === 'platform') {
      return t('profile:passkeys.platformAuthenticator');
    }
    return `${t('profile:passkeys.securityKey')} ${index + 1}`;
  };

  // Don't render if passkeys not supported
  if (!isPasskeySupported()) {
    return null;
  }

  return (
    <div data-testid={dataTestId}>
      {error && (
        <Alert
          type="error"
          message={error}
          className="mb-4"
          data-testid="passkey-error"
        />
      )}
      {success && (
        <Alert
          type="success"
          message={success}
          className="mb-4"
          data-testid="passkey-success"
        />
      )}

      <div className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('profile:passkeys.description')}
        </p>

        {/* Passkey list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            {t('profile:passkeys.loadError')}
          </div>
        ) : passkeys.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <FingerprintIcon className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('profile:passkeys.noPasskeys')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {passkeys.map((passkey, index) => {
              const added = addedOn(passkey.createdAt);
              return (
                <div
                  key={passkey.credentialId}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  data-testid={`passkey-item-${index}`}
                >
                  <div className="flex items-center space-x-3">
                    {getPasskeyIcon(passkey)}
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {getPasskeyName(passkey, index)}
                      </p>
                      {added && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {added}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(passkey.credentialId)}
                    disabled={deleteMutation.isPending}
                    aria-label={t('profile:passkeys.delete')}
                    data-testid={`passkey-delete-${index}`}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add passkey button */}
        <Button
          variant="outline"
          onClick={handleRegister}
          disabled={registerMutation.isPending || passkeys.length >= 20}
          className="w-full"
          data-testid="add-passkey-button"
        >
          {registerMutation.isPending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          ) : (
            <PlusIcon className="w-4 h-4 mr-2" />
          )}
          {t('profile:passkeys.addPasskey')}
        </Button>

        {passkeys.length >= 20 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t('profile:passkeys.maxReached')}
          </p>
        )}
      </div>
    </div>
  );
}
